import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { X, Mail, Linkedin, Instagram, Twitter, ExternalLink, ArrowRight } from 'lucide-react';
import "./HomeImpact.css";
import "./TeamSection.css";

const teamMembers = [
  {
    id: 1,
    name: "Dra. Maica Amador",
    role: "CEO & Co-Founder",
    quote: "Investigadora vinculada al Instituto de Cibernética, Empresa y Sociedad (IUCES-ULPGC) y a Vilnius Tech, Maica lidera Sparkling Tech Solutions desde una sólida trayectoria en investigación aplicada, innovación y gestión de proyectos tecnológicos. Es la impulsora de la visión científica y estratégica de la empresa, coordinando el desarrollo de soluciones basadas en neurociencia, inteligencia artificial y tecnologías inmersivas, con un firme enfoque en el impacto social, el rigor científico y la transferencia de conocimiento.",
    image: "/img/Maica.png",
    color: "purple",
    linkedin: "https://www.linkedin.com/in/maicaamador/",
  },
  {
    id: 2,
    name: "Carlos Amador",
    role: "COO & Co-Founder",
    quote: "Estratega con 30 años de experiencia en gestión operativa y desarrollo de negocio. Carlos es el pilar que garantiza la escalabilidad de nuestros proyectos, aplicando una visión experta en la optimización de procesos y la creación de alianzas que maximizan el valor tecnológico de Sparkling Tech.",
    image: "/img/Carlos.png",
    color: "purple",
    linkedin: "https://www.linkedin.com/company/sparklingtechsolutions/",
  },
  {
    id: 3,
    name: "Carmen Marrero",
    role: "CFO & Co-Founder",
    quote: "Experta en gestión financiera y administrativa, Carmen asegura la solidez y viabilidad de Sparkling Tech Solutions. Su rigor y atención al detalle son fundamentales para garantizar que cada recurso se utilice de manera más eficiente, apoyando la misión de transformar la sociedad a través de la ciencia y la tecnología.",
    image: "/img/Carmen.png",
    color: "purple",
    linkedin: "#",
    email: "carmen.marrero@sparklingtech.es",
  },
  {
    id: 4,
    name: "Javier Amador",
    role: "Project Manager",
    quote: "Con una sólida formación en gestión de proyectos tecnológicos, Javier coordina de manera efectiva a los equipos multidisciplinares para asegurar el cumplimiento de hitos y la calidad en cada entrega. Su capacidad de organización y resolución de problemas es clave para convertir las ideas en realidades tangibles.",
    image: "/img/Javier.png",
    color: "purple",
    linkedin: "#",
    email: "javier@sparklingtech.es",
  },
  {
    id: 5,
    name: "Dakota Molina",
    role: "Product Manager",
    quote: "Dakota combina experiencia en formación tecnológica, diseño gráfico y desarrollo web. Desde 2023 ejerce como profesora en distintos centros de formación en Canarias, participando en programas vinculados al ámbito digital y tecnológico. Cuenta con trayectoria profesional en diseño, programación y expansión digital de proyectos online, aportando al equipo una visión creativa, técnica y orientada a la experiencia digital.",
    image: "/img/Dakota.png",
    color: "purple",
    linkedin: "https://www.linkedin.com/in/dakota-molina/",
  },
  {
    id: 6,
    name: "Arturo Domínguez",
    role: "Data Analyst",
    quote: "Con una amplia trayectoria en el ámbito sanitario, Arturo combina su experiencia como técnico de laboratorio y enfermero con un enfoque especializado en análisis de datos. Su perfil híbrido le permite interpretar la información clínica desde una perspectiva práctica y analítica. Su capacidad para conectar la realidad clínica con el análisis riguroso lo convierte en un perfil clave dentro de Sparkling Tech.",
    image: "/img/Arturo.png",
    color: "purple",
    linkedin: "#",
    email: "arturo@sparklingtech.es",
  },
  {
    id: 7,
    name: "Paula Losciale",
    role: "Fullstack developer",
    quote: "Especialista en desarrollo web fullstack y UI/UX, Paula combina la lógica del backend con el detalle visual del frontend para crear experiencias digitales funcionales, atractivas y centradas en el usuario. Su versatilidad técnica y sensibilidad de diseño la convierten en una pieza clave para construir productos sólidos, escalables y con identidad propia en Sparkling Tech, aportando cohesión, creatividad y una mirada integral a cada fase del proyecto.",
    image: "/img/Paula.png",
    color: "purple",
    linkedin: "https://www.linkedin.com/in/paula-losciale/",
    email: "paula@sparklingtech.es",
  },
];

const colorClasses = {
  pink: {
    bg: "bg-[oklch(0.92_0.08_350)]",
    border: "border-[oklch(0.70_0.22_330)]",
    text: "text-[oklch(0.40_0.20_330)]",
    ring: "ring-[oklch(0.70_0.22_330)]",
    badge: "bg-[oklch(0.70_0.22_330)]",
    shadow: "shadow-[0_8px_30px_oklch(0.70_0.22_330_/_0.15)]",
  },
  blue: {
    bg: "bg-[oklch(0.92_0.06_250)]",
    border: "border-[oklch(0.60_0.18_260)]",
    text: "text-[oklch(0.35_0.16_260)]",
    ring: "ring-[oklch(0.60_0.18_260)]",
    badge: "bg-[oklch(0.60_0.18_260)]",
    shadow: "shadow-[0_8px_30px_oklch(0.60_0.18_260_/_0.15)]",
  },
  purple: {
    bg: "bg-[oklch(0.92_0.07_290)]",
    border: "border-[oklch(0.60_0.20_290)]",
    text: "text-[oklch(0.35_0.18_290)]",
    ring: "ring-[oklch(0.60_0.20_290)]",
    badge: "bg-[oklch(0.60_0.20_290)]",
    shadow: "shadow-[0_8px_30px_oklch(0.60_0.20_290_/_0.15)]",
  },
};

const SPRING_OPTIONS = {
  type: "spring",
  stiffness: 350,
  damping: 25,
  mass: 0.5,
};

function CompactCard({ member, onClick, colors, isHidden, uniqueKey }) {
  return (
    <motion.div
      layoutId={`card-container-${uniqueKey}`}
      onClick={onClick}
      style={{ opacity: isHidden ? 0 : 1 }} 
      whileHover={{ scale: 1.02 }}
      className={`
        relative h-[250px] rounded-[2rem] p-6 cursor-pointer
        ${colors.bg} ${colors.border} border-2
        flex flex-col items-center justify-start text-center
        ${colors.shadow}
      `}
    >
      <motion.div 
        layoutId={`avatar-container-${uniqueKey}`}
        className="relative w-32 h-32 mb-6 rounded-full overflow-hidden border-4 border-white shadow-sm"
      >
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover select-none pointer-events-none"
          draggable="false"
        />
      </motion.div>

      <div className="flex flex-col items-center">
        <motion.h3 
          layoutId={`title-${uniqueKey}`}
          className="font-black text-foreground text-xl md:text-2xl mb-2 tracking-tight"
        >
          {member.name}
        </motion.h3>
      </div>

      <div className="member-badge-container">
        <motion.span 
          layoutId={`badge-${uniqueKey}`}
          className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-sm uppercase tracking-wide ${colors.badge}`}
        >
          {member.role}
        </motion.span>
      </div>
    </motion.div>
  );
}

function ExpandedCardOverlay({ member, onClose, colors, uniqueKey }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-10 pt-10 md:pt-20 overflow-y-auto pointer-events-none">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm cursor-pointer pointer-events-auto"
      />

      <motion.div
        layoutId={`card-container-${uniqueKey}`}
        className={`
          relative w-full max-w-4xl bg-white rounded-[2.5rem]
          ${colors.bg} ${colors.border} border-2
          shadow-2xl overflow-hidden pointer-events-auto select-none cursor-default mb-10
        `}
        transition={SPRING_OPTIONS}
      >
        <div className="p-8 md:p-12 flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-start relative">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full hover:bg-black/5 transition-colors z-50"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6 text-foreground/60" />
          </button>

        <motion.div 
          layoutId={`avatar-container-${uniqueKey}`}
          className="relative w-40 h-40 md:w-64 md:h-64 flex-shrink-0 rounded-full overflow-hidden border-8 border-white shadow-xl"
          transition={SPRING_OPTIONS}
        >
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover select-none pointer-events-none"
            draggable="false"
          />
        </motion.div>

        <div className="flex flex-col flex-1 items-center md:items-start text-center md:text-left pt-2 md:pt-4 w-full">
          <motion.h3 
            layoutId={`title-${uniqueKey}`}
            className="font-black text-foreground text-3xl md:text-5xl mb-4 tracking-tight leading-none"
          >
            {member.name}
          </motion.h3>
          
          <motion.span 
            layoutId={`badge-${uniqueKey}`}
            className={`inline-block px-5 py-2 rounded-full text-sm font-bold text-white shadow-sm uppercase tracking-wide mb-8 ${colors.badge}`}
          >
            {member.role}
          </motion.span>

          <div className="mt-2 w-full">
            <p className={`text-base md:text-xl font-medium leading-relaxed italic ${colors.text}`}>
              &ldquo;{member.quote}&rdquo;
            </p>
            
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-8 md:mt-10 pb-2">
              {member.linkedin && member.linkedin !== "#" && (
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`
                    flex items-center justify-center gap-3 px-6 py-3 min-w-[180px] rounded-full 
                    text-white font-bold shadow-lg transition-all hover:scale-105 active:scale-95
                    ${colors.badge}
                  `}
                >
                  <LinkedInIcon className="w-5 h-5" />
                  <span>Conectar en LinkedIn</span>
                </a>
              )}
              {member.email && member.email !== "#" && (
                <a
                  href={`mailto:${member.email}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`
                    flex items-center justify-center gap-3 px-6 py-3 min-w-[180px] rounded-full 
                    text-white font-bold shadow-lg transition-all hover:scale-105 active:scale-95
                    ${colors.badge}
                  `}
                >
                  <Mail className="w-5 h-5" />
                  <span>Contactar por Email</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  </div>
);
}

function LinkedInIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

const TeamCarouselRow = ({ members, onMemberClick, selectedUniqueKey, direction = -1 }) => {
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const x = useMotionValue(0);
  const velocityRef = useRef(0);
  const initializedRef = useRef(false);

  const tripleMembers = [...members, ...members, ...members];

  useEffect(() => {
    const oneSetWidth = scrollRef.current ? scrollRef.current.scrollWidth / 3 : 0;
    
    const unsubscribe = x.on("change", (latest) => {
      if (oneSetWidth <= 0) return;
      
      // Handle infinite loop jumps - occurs immediately on change
      if (latest <= -2 * oneSetWidth) {
        x.set(latest + oneSetWidth);
      } else if (latest >= 0) {
        x.set(latest - oneSetWidth);
      }
    });

    return () => unsubscribe();
  }, [x]);

  useEffect(() => {
    let animationId;
    
    const animate = () => {
      if (scrollRef.current) {
        const oneSetWidth = scrollRef.current.scrollWidth / 3;
        
        if (!initializedRef.current && oneSetWidth > 0) {
          x.set(-oneSetWidth);
          initializedRef.current = true;
        }

        // Only apply automatic movement if NOT dragging
        if (!isDragging) {
          const currentX = x.get();
          const baseSpeed = isPaused ? 0 : 0.6 * direction;
          let nextX = currentX + baseSpeed + velocityRef.current;
          x.set(nextX);
          
          velocityRef.current *= 0.95;
          if (Math.abs(velocityRef.current) < 0.05) velocityRef.current = 0;
        }
      }
      
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, isDragging, direction, x]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      velocityRef.current += e.deltaY * 0.15 * direction;
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [direction]);

  return (
    <div 
      className="team-carousel-wrapper"
      ref={containerRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div 
        className="team-carousel-track" 
        ref={scrollRef}
        style={{ x }}
        drag="x"
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(e, info) => {
          setIsDragging(false);
          // Transfer drag velocity to our internal velocity tracker
          velocityRef.current = info.velocity.x * 0.01;
        }}
        dragElastic={0}
      >
        {tripleMembers.map((member, index) => {
          const uniqueKey = `${member.id}-${index}`;
          return (
            <div className="team-carousel-item" key={uniqueKey}>
              <CompactCard 
                member={member}
                uniqueKey={uniqueKey}
                colors={colorClasses[member.color]}
                onClick={() => {
                  if (!isDragging) {
                    onMemberClick(member.id, uniqueKey);
                  }
                }}
                isHidden={selectedUniqueKey === uniqueKey}
              />
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

export function TeamSection() {
  const [selection, setSelection] = useState({ id: null, uniqueKey: null });
  const selectedMember = teamMembers.find(m => m.id === selection.id);

  const handleMemberClick = (id, uniqueKey) => {
    setSelection({ id, uniqueKey });
  };

  return (
    <section className="pt-10 pb-4 md:pt-16 md:pb-8 bg-background relative z-10">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-8 md:mb-2">
          <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight" style={{ 
            fontSize: "3.2rem", 
            fontWeight: 900,
            background: "linear-gradient(135deg, #6366f1 0%, #a855f1 50%, #ec4899 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-0.02em",
            marginBottom: "1.5rem",
            lineHeight: "1.2"
          }}>
            Nuestro motor es el equipo:
          </h2>
          <p className="text-muted-foreground text-lg md:text-2xl max-w-3xl mx-auto font-medium leading-relaxed" style={{
            fontSize: "1.25rem",
            lineHeight: "1.6",
            color: "#4b5563",
            maxWidth: "700px",
            margin: "0 auto"
          }}>
            Profesionales de distintas áreas que trabajan con un objetivo común.
          </p>
        </div>

        <TeamCarouselRow 
          members={teamMembers} 
          onMemberClick={handleMemberClick} 
          selectedUniqueKey={selection.uniqueKey}
          direction={-1} 
        />

        <AnimatePresence>
          {selection.id && selectedMember && (
            <ExpandedCardOverlay 
              member={selectedMember}
              uniqueKey={selection.uniqueKey}
              colors={colorClasses[selectedMember.color]}
              onClose={() => setSelection({ id: null, uniqueKey: null })}
            />
          )}
        </AnimatePresence>

        <div className="home-impact-cta mt-4 mb-5" data-aos="fade-up">
          <Link to="/about" className="lit-up-button-impact">
            <div className="lit-up-border-impact" />
            <div className="lit-up-content-impact">
              Conoce más sobre nosotros
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
